/**
 * SatNOGS live observations, ported from loader-satnogs-telem.js: the 20
 * latest observations linked to network.satnogs.org, polled every 60 s, and a
 * 2.5 s pulse on the globe at the originating station for each observation
 * seen for the first time.
 */
import {
  getSatNogsObservations,
  getSatNogsStations,
  type SatNogsList,
  type SatNogsObservationRow,
  type SatNogsStationRow,
} from './feeds';
import { useAwareness, type SatNogsObservation, type SatNogsStation } from './store';
import { errorMessage, parseMs, startPolling } from './util';

export const SATNOGS_REFRESH_MS = 60 * 1000;
export const OBSERVATION_LIMIT = 20;
export const PULSE_MS = 2500;

export function observationUrl(id: number): string {
  return `https://network.satnogs.org/observations/${id}/`;
}

/** SatNOGS lists are paginated ({results}) or bare arrays. */
export function listItems<T>(resp: SatNogsList<T> | null | undefined): T[] {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  return Array.isArray(resp.results) ? resp.results : [];
}

export function stationIndex(rows: SatNogsStationRow[]): Map<number, SatNogsStation> {
  const index = new Map<number, SatNogsStation>();
  for (const s of rows) {
    if (!s || s.id === null || s.id === undefined) continue;
    index.set(s.id, { id: s.id, lat: s.lat ?? null, lon: s.lng ?? null, name: s.name || '' });
  }
  return index;
}

export function observationStatus(o: Pick<SatNogsObservationRow, 'vetted_status' | 'status'>): string {
  return o.vetted_status || o.status || 'unknown';
}

export function summariseObservation(o: SatNogsObservationRow): SatNogsObservation {
  return {
    id: o.id,
    startMs: parseMs(o.start ?? null),
    satellite: o.tle0 || 'NORAD ' + (o.norad_cat_id ?? '?'),
    status: observationStatus(o),
    stationId: o.ground_station ?? null,
  };
}

export interface StationPulse {
  observationId: number;
  stationId: number;
  lat: number;
  lon: number;
}

/**
 * Pulses for observations not seen before whose station has a position. Adds
 * every observation id to `seen` (including those without a located station,
 * as the original did).
 */
export function newPulses(
  observations: SatNogsObservation[],
  stations: Map<number, SatNogsStation>,
  seen: Set<number>,
): StationPulse[] {
  const pulses: StationPulse[] = [];
  for (const o of observations) {
    if (seen.has(o.id)) continue;
    seen.add(o.id);
    if (o.stationId === null) continue;
    const station = stations.get(o.stationId);
    if (!station || station.lat === null || station.lon === null) continue;
    pulses.push({ observationId: o.id, stationId: o.stationId, lat: station.lat, lon: station.lon });
  }
  return pulses;
}

/**
 * Load the station index once (a failure leaves the list working without
 * pulses), then poll observations every 60 s until stopped, handing new
 * pulses to onPulse. The list is cleared on stop.
 */
export function startSatNogs(onPulse: (pulse: StationPulse) => void): () => void {
  let stopped = false;
  let stopPolling: (() => void) | null = null;
  const seen = new Set<number>();
  let stations = new Map<number, SatNogsStation>();

  const refresh = async () => {
    const store = useAwareness.getState();
    try {
      const observations = listItems(await getSatNogsObservations()).map(summariseObservation);
      if (stopped) return;
      store.setSatNogs({ observations, stationCount: stations.size, error: null, updatedMs: Date.now() });
      for (const pulse of newPulses(observations, stations, seen)) onPulse(pulse);
    } catch (err) {
      if (stopped) return;
      console.warn('[satnogs] observations feed unavailable:', err);
      const previous = useAwareness.getState().satnogs;
      store.setSatNogs({
        observations: previous?.observations ?? [],
        stationCount: stations.size,
        error: errorMessage(err),
        updatedMs: previous?.updatedMs ?? null,
      });
    }
  };

  (async () => {
    try {
      stations = stationIndex(listItems(await getSatNogsStations()));
    } catch (err) {
      console.warn('[satnogs] stations feed unavailable; observations will list without globe pulses:', err);
    }
    if (stopped) return;
    stopPolling = startPolling(() => {
      void refresh();
    }, SATNOGS_REFRESH_MS);
  })();

  return () => {
    stopped = true;
    stopPolling?.();
    useAwareness.getState().setSatNogs(null);
  };
}
